import { useState } from 'react';
import TelegramIcon from '@mui/icons-material/Telegram';
import { Button } from '@mui/material';
import { AppConfig } from '../../../Config';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';
import UsersService from '../../common/Rest/Services/UserService/UsersService';
import { TelegramLoginData, TelegramWidgetAuthData } from '../../common/Rest/Services/UserService/Data';

interface TelegramLoginButtonProps {
  disabled?: boolean;
  onAuthenticated: (token: string) => void;
  onError: (message: string) => void;
}

interface TelegramDebugEntry {
  time: string;
  step: string;
  details?: Record<string, unknown>;
}

type TelegramPopupAuthResult =
  | { kind: 'idToken'; idToken: string }
  | { kind: 'widgetAuth'; widgetAuth: TelegramWidgetAuthData };

declare global {
  interface Window {
    __readerlyTelegramLoginDebug?: TelegramDebugEntry[];
  }
}

class TelegramLoginFlowError extends Error {
  code: string;
  details?: Record<string, unknown>;

  constructor(code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'TelegramLoginFlowError';
    this.code = code;
    this.details = details;
    Object.setPrototypeOf(this, TelegramLoginFlowError.prototype);
  }
}

const TELEGRAM_OIDC_ORIGIN = 'https://oauth.telegram.org';
const TELEGRAM_OIDC_AUTH_URL = `${TELEGRAM_OIDC_ORIGIN}/auth`;
const TELEGRAM_DEBUG_STORAGE_KEY = 'readerly:telegram-debug';

const isTelegramDebugEnabled = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has('telegramDebug') || localStorage.getItem(TELEGRAM_DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const logTelegramDebug = (enabled: boolean, step: string, details?: Record<string, unknown>) => {
  if (!enabled) {
    return;
  }

  const entry: TelegramDebugEntry = {
    time: new Date().toISOString(),
    step,
    details,
  };
  window.__readerlyTelegramLoginDebug = [...(window.__readerlyTelegramLoginDebug || []), entry].slice(-80);
  console.debug('[Readerly Telegram Login]', step, details || '');
};

const getPayloadPreview = (payload: unknown): string => {
  if (typeof payload === 'string') {
    return payload.slice(0, 180);
  }

  if (payload && typeof payload === 'object') {
    try {
      return JSON.stringify(payload).slice(0, 180);
    } catch {
      return '[object payload]';
    }
  }

  return String(payload ?? '');
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getAuthResultIdToken = (data: unknown): string | null => {
  if (!isRecord(data)) {
    return null;
  }

  const result = data.result;
  const nestedResult = isRecord(result) ? result : null;
  const candidates = [
    data.id_token,
    typeof result === 'string' ? result : null,
    nestedResult?.id_token,
    nestedResult?.result,
    nestedResult?.token,
  ];

  const idToken = candidates.find((candidate) => typeof candidate === 'string' && candidate.length > 0);
  return typeof idToken === 'string' ? idToken : null;
};

const getTelegramWidgetAuthData = (data: unknown): TelegramWidgetAuthData | null => {
  const result = isRecord(data) && isRecord(data.result) ? data.result : null;
  if (!result) {
    return null;
  }

  const id = typeof result.id === 'number' ? result.id : Number(result.id);
  const authDate = typeof result.auth_date === 'number' ? result.auth_date : Number(result.auth_date);
  if (!Number.isFinite(id) || !Number.isFinite(authDate) || typeof result.hash !== 'string' || !result.hash) {
    return null;
  }

  return {
    id,
    first_name: typeof result.first_name === 'string' ? result.first_name : undefined,
    last_name: typeof result.last_name === 'string' ? result.last_name : undefined,
    username: typeof result.username === 'string' ? result.username : undefined,
    photo_url: typeof result.photo_url === 'string' ? result.photo_url : undefined,
    auth_date: authDate,
    hash: result.hash,
  };
};

const getTelegramAuthResult = (data: unknown): TelegramPopupAuthResult | null => {
  const idToken = getAuthResultIdToken(data);
  if (idToken) {
    return { kind: 'idToken', idToken };
  }

  const widgetAuth = getTelegramWidgetAuthData(data);
  return widgetAuth ? { kind: 'widgetAuth', widgetAuth } : null;
};

const getSafeAuthResultKeys = (data: unknown): Record<string, unknown> => {
  if (!isRecord(data)) {
    return {
      dataType: typeof data,
    };
  }

  const record = data;
  const keys = Object.keys(record);
  const fieldTypes = keys.reduce<Record<string, string>>((acc, key) => {
    acc[key] = typeof record[key];
    return acc;
  }, {});
  const result = isRecord(record.result) ? record.result : null;

  return {
    dataType: 'object',
    dataKeys: keys,
    fieldTypes,
    resultKeys: result ? Object.keys(result) : null,
    resultFieldTypes: result
      ? Object.keys(result).reduce<Record<string, string>>((acc, key) => {
          acc[key] = typeof result[key];
          return acc;
        }, {})
      : null,
    hasCode: typeof record.code === 'string',
    hasResultUrl: typeof record.result_url === 'string',
    hasUrl: typeof record.url === 'string',
    hasToken: typeof record.token === 'string',
    hasNestedIdToken: typeof result?.id_token === 'string',
  };
};

const getTelegramFlowUserMessage = (error: TelegramLoginFlowError): string => {
  switch (error.code) {
    case 'popup-blocked':
      return 'Браузер заблокував Telegram popup. Дозвольте popups для Readerly і спробуйте ще раз.';
    case 'popup-closed-without-auth-result':
      return 'Telegram вікно закрилося без відповіді. Спробуйте ще раз.';
    case 'missing-id-token':
      return 'Telegram відповів, але не передав підтвердження входу. Спробуйте ще раз.';
    default:
      return 'Не вдалося увійти через Telegram. Спробуйте ще раз.';
  }
};

const authenticateWithTelegram = (clientId: number, nonce: string, debugEnabled: boolean): Promise<TelegramPopupAuthResult> =>
  new Promise((resolve, reject) => {
    const width = 550;
    const height = 650;
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
    const authUrl = new URL(TELEGRAM_OIDC_AUTH_URL);
    const redirectUri = `${window.location.origin}/login`;

    authUrl.searchParams.set('response_type', 'post_message');
    authUrl.searchParams.set('client_id', clientId.toString());
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('origin', window.location.origin);
    authUrl.searchParams.set('scope', 'openid profile');
    authUrl.searchParams.set('nonce', nonce);
    authUrl.searchParams.set('lang', 'uk');

    logTelegramDebug(debugEnabled, 'popup-auth-url-built', {
      clientId,
      origin: window.location.origin,
      redirectUri,
      scope: 'openid profile',
      nonceLength: nonce.length,
    });

    let popup: Window | null = null;
    let closeTimer = 0;
    let closeGraceTimer = 0;
    let finished = false;

    const cleanup = () => {
      finished = true;
      window.removeEventListener('message', handleMessage);
      window.clearInterval(closeTimer);
      window.clearTimeout(closeGraceTimer);
      if (popup && !popup.closed) {
        popup.close();
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== TELEGRAM_OIDC_ORIGIN) {
        return;
      }

      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          logTelegramDebug(debugEnabled, 'telegram-message-json-parse-failed', {
            origin: event.origin,
            dataLength: data.length,
          });
          return;
        }
      }

      logTelegramDebug(debugEnabled, 'telegram-message-received', {
        origin: event.origin,
        eventName: data?.event,
        hasResult: typeof data?.result === 'string',
        hasIdToken: typeof data?.id_token === 'string',
        error: typeof data?.error === 'string' ? data.error : null,
        ...getSafeAuthResultKeys(data),
      });

      if (data?.event !== 'auth_result') {
        return;
      }

      cleanup();
      const authResult = getTelegramAuthResult(data);
      if (authResult?.kind === 'idToken') {
        logTelegramDebug(debugEnabled, 'telegram-id-token-received', {
          tokenLength: authResult.idToken.length,
          tokenParts: authResult.idToken.split('.').length,
        });
        resolve(authResult);
        return;
      }

      if (authResult?.kind === 'widgetAuth') {
        logTelegramDebug(debugEnabled, 'telegram-widget-auth-received', {
          id: authResult.widgetAuth.id,
          authDate: authResult.widgetAuth.auth_date,
          hasFirstName: Boolean(authResult.widgetAuth.first_name),
          hasLastName: Boolean(authResult.widgetAuth.last_name),
          hasUsername: Boolean(authResult.widgetAuth.username),
          hasPhotoUrl: Boolean(authResult.widgetAuth.photo_url),
          hashLength: authResult.widgetAuth.hash.length,
        });
        resolve(authResult);
        return;
      }

      reject(
        new TelegramLoginFlowError('missing-id-token', data.error || 'Telegram auth_result did not include id_token', {
          eventName: data?.event,
          error: data?.error || null,
          hasResult: Boolean(data?.result),
          hasIdToken: Boolean(data?.id_token),
          ...getSafeAuthResultKeys(data),
        })
      );
    };

    window.addEventListener('message', handleMessage);

    popup = window.open(
      authUrl.toString(),
      'telegram_oidc_login',
      [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'status=0',
        'location=0',
        'menubar=0',
        'toolbar=0',
      ].join(',')
    );

    if (!popup) {
      window.removeEventListener('message', handleMessage);
      reject(new TelegramLoginFlowError('popup-blocked', 'Telegram login popup was blocked'));
      return;
    }

    logTelegramDebug(debugEnabled, 'popup-opened');

    closeTimer = window.setInterval(() => {
      if (!finished && popup?.closed) {
        window.clearInterval(closeTimer);
        logTelegramDebug(debugEnabled, 'popup-closed-observed');
        closeGraceTimer = window.setTimeout(() => {
          if (!finished) {
            cleanup();
            reject(
              new TelegramLoginFlowError(
                'popup-closed-without-auth-result',
                'Telegram popup closed before auth_result message was received'
              )
            );
          }
        }, 3000);
      }
    }, 250);

    popup.focus();
  });

export const requestTelegramLoginData = async (): Promise<TelegramLoginData> => {
  const clientId = Number(AppConfig.TelegramLoginClientId);
  const debugEnabled = isTelegramDebugEnabled();

  if (!clientId) {
    throw new TelegramLoginFlowError('not-configured', 'Telegram login is not configured');
  }

  logTelegramDebug(debugEnabled, 'nonce-request-start');
  const { nonce } = await UsersService.getTelegramLoginNonce();
  logTelegramDebug(debugEnabled, 'nonce-request-success', { nonceLength: nonce.length });

  const authResult = await authenticateWithTelegram(clientId, nonce, debugEnabled);
  const payload: TelegramLoginData =
    authResult.kind === 'idToken'
      ? { idToken: authResult.idToken, nonce }
      : { widgetAuth: authResult.widgetAuth };

  logTelegramDebug(debugEnabled, 'readerly-telegram-auth-payload-ready', {
    method: authResult.kind,
    idTokenLength: authResult.kind === 'idToken' ? authResult.idToken.length : null,
    idTokenParts: authResult.kind === 'idToken' ? authResult.idToken.split('.').length : null,
    hasWidgetHash: authResult.kind === 'widgetAuth',
  });

  return payload;
};

const TelegramLoginButton: React.FC<TelegramLoginButtonProps> = ({ disabled, onAuthenticated, onError }) => {
  const [submitting, setSubmitting] = useState(false);
  const clientId = Number(AppConfig.TelegramLoginClientId);
  const debugEnabled = isTelegramDebugEnabled();

  if (!clientId) {
    return null;
  }

  const handleTelegramLogin = async () => {
    try {
      setSubmitting(true);
      onError('');

      const loginPayload = await requestTelegramLoginData();

      logTelegramDebug(debugEnabled, 'readerly-telegram-login-start', {
        method: loginPayload.idToken ? 'idToken' : 'widgetAuth',
        idTokenLength: loginPayload.idToken?.length ?? null,
        idTokenParts: loginPayload.idToken?.split('.').length ?? null,
        hasWidgetHash: Boolean(loginPayload.widgetAuth?.hash),
      });

      const response = await UsersService.loginWithTelegram(loginPayload);
      logTelegramDebug(debugEnabled, 'readerly-telegram-login-success');
      onAuthenticated(response.token);
    } catch (error) {
      logTelegramDebug(debugEnabled, 'telegram-login-error', {
        name: error instanceof Error ? error.name : typeof error,
        message: error instanceof Error ? error.message : String(error),
        code: error instanceof TelegramLoginFlowError ? error.code : null,
        status: error instanceof ApiError ? error.status : null,
        payloadPreview: error instanceof ApiError ? getPayloadPreview(error.payload) : null,
      });

      if (error instanceof ApiError) {
        onError(
          debugEnabled
            ? `DEBUG Telegram: Readerly API відхилив token. Status: ${error.status}. Payload: ${getPayloadPreview(error.payload)}`
            : `Telegram підтвердив вхід, але Readerly відхилив токен (${error.status}).`
        );
      } else if (error instanceof TelegramLoginFlowError) {
        onError(
          debugEnabled
            ? `DEBUG Telegram: ${error.code}. ${error.message}`
            : getTelegramFlowUserMessage(error)
        );
      } else {
        onError(
          debugEnabled && error instanceof Error
            ? `DEBUG Telegram: unexpected-error. ${error.name}: ${error.message}`
            : 'Не вдалося увійти через Telegram. Спробуйте ще раз.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Button
      className="telegram-login-button"
      type="button"
      variant="outlined"
      startIcon={<TelegramIcon />}
      onClick={handleTelegramLogin}
      disabled={disabled || submitting}
      fullWidth
    >
      {submitting ? 'Підключаємо Telegram...' : 'Увійти через Telegram'}
    </Button>
  );
};

export default TelegramLoginButton;
